import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button, styled } from '@mui/material';

const Section = styled(Box)({
    position: 'relative',
    padding: '120px 0 40px',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFF 100%)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '10%',
        left: '0',
        width: '100%',
        height: '80%',
        background: 'radial-gradient(ellipse 60% 40% at 30% 50%, rgba(0, 102, 255, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '20%',
        right: '5%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none',
    },
    '@media (max-width: 968px)': {
        minHeight: 'auto',
        padding: '110px 0 44px',
    },
    '@media (max-width: 600px)': {
        padding: '100px 0 36px',
    },
});

const SectionGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '80px',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    '@media (max-width: 968px)': {
        gridTemplateColumns: '1fr',
        gap: '48px',
    },
});

const Content = styled(Box)({
    textAlign: 'left',
    '@media (max-width: 968px)': {
        textAlign: 'center',
    },
});

const Title = styled(Typography)({
    fontSize: '60px',
    fontWeight: 600,
    lineHeight: 1.1,
    marginBottom: '28px',
    color: '#111827',
    letterSpacing: '-2px',
    '@media (max-width: 968px)': {
        fontSize: '48px',
        letterSpacing: '-1.5px',
    },
    '@media (max-width: 600px)': {
        fontSize: '38px',
        letterSpacing: '-1.2px',
    },
    '@media (max-width: 480px)': {
        fontSize: '32px',
        letterSpacing: '-1px',
    },
});

const Description = styled(Typography)({
    fontSize: '20px',
    lineHeight: 1.6,
    color: '#6B7280',
    textAlign: 'justify',
    marginBottom: '40px',
    maxWidth: '680px',
    '@media (max-width: 968px)': {
        fontSize: '18px',
        margin: '0 auto 32px',
        maxWidth: '100%',
    },
    '@media (max-width: 600px)': {
        fontSize: '16px',
    },
});

const CtaRow = styled(Box)({
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    '@media (max-width: 968px)': {
        flexDirection: 'column',
        width: '100%',
    },
});

const PrimaryButton = styled(Button)({
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #0066FF, #0052CC)',
    color: '#FFFFFF',
    minWidth: '190px',
    boxShadow: '0 4px 20px rgba(0, 102, 255, 0.3)',
    '&:hover': {
        background: 'linear-gradient(135deg, #0052CC, #003D99)',
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 28px rgba(0, 102, 255, 0.35)',
    },
    '@media (max-width: 968px)': {
        width: '100%',
    },
});

// const SecondaryButton = styled(Button)({
//     padding: '16px 32px',
//     fontSize: '15px',
//     fontWeight: 500,
//     borderRadius: '12px',
//     textTransform: 'none',
//     color: '#374151',
//     borderColor: '#D1D5DB',
//     borderWidth: '1.5px',
//     background: '#FFFFFF',
//     minWidth: '230px',
//     '&:hover': {
//         borderColor: '#0066FF',
//         color: '#0066FF',
//         background: 'rgba(0, 102, 255, 0.03)',
//     },
//     '@media (max-width: 968px)': {
//         width: '100%',
//     },
// });

const Visual = styled(Box)({
    position: 'relative',
});

const VisualCard = styled(Box)({
    position: 'relative',
    width: '100%',
    background: '#FFFFFF',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.04)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-2px',
        borderRadius: '24px',
        padding: '2px',
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.3), rgba(99, 102, 241, 0.2))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.65,
    },
    '& img': {
        width: '100%',
        height: 'auto',
        display: 'block',
        position: 'relative',
        zIndex: 1,
    },
    '@media (max-width: 600px)': {
        borderRadius: '20px',
        '&::before': {
            borderRadius: '20px',
        },
    },
});

const HeaderSection = () => {
    return (
        <Section component="section" aria-label="Platform header">
            <Container maxWidth="xl">
                <SectionGrid>
                    <Content>
                        <Title variant="h1">
                            One system. Every visit verified. Every payout clean
                        </Title>
                        <Description>
                            Your ops head sees the full operations view -- dispatch, field activity, payout queue. Your therapists
                            see their schedule, session workflow, and documentation. One app, role-based views, nothing depending
                            on what someone reported.
                        </Description>
                        <CtaRow>
                            <PrimaryButton
                                component={Link}
                                to="/"
                                state={{ homeSection: 'demo-video' }}
                                variant="contained"
                            >
                                See how it works
                            </PrimaryButton>
                        </CtaRow>
                    </Content>

                    <Visual>
                        <VisualCard>
                            <img src={`${process.env.PUBLIC_URL}/assets/Dashboard.png`} alt="Admin portal screenshot" />
                        </VisualCard>
                    </Visual>
                </SectionGrid>
            </Container>
        </Section>
    );
};

export default HeaderSection;
